/**
 * Solid, non-emoji weather glyphs used for the "time-of-day" indicators
 * (Bye bye avatar badge, greetings, etc.).
 *
 *   • Sun   → classic 8-ray sun, solid yellow  #FFC107  (morning / afternoon)
 *   • Moon  → classic crescent moon, solid    #90B4E8  (evening / night)
 *
 * Kept intentionally minimal — no props beyond size / className so it can be
 * dropped anywhere Lucide icons used to sit.
 */
export function SunIcon({ size = 24, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      data-testid="weather-sun-icon"
    >
      {/* 8 rays, evenly spaced (0/45/90/…/315°) */}
      <g fill="#FFC107">
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <rect
            key={deg}
            x="11"
            y="1"
            width="2"
            height="4"
            rx="1"
            transform={`rotate(${deg} 12 12)`}
          />
        ))}
      </g>
      {/* Solid disc */}
      <circle cx="12" cy="12" r="5.2" fill="#FFC107" />
    </svg>
  );
}

export function MoonIcon({ size = 24, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      data-testid="weather-moon-icon"
    >
      {/* Classic crescent: outer circle minus offset inner circle (via path). */}
      <path
        d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5z"
        fill="#90B4E8"
      />
    </svg>
  );
}

/**
 * Returns { period, label, isNight } for a given hour.
 *   • 5h–11h59  → matin
 *   • 12h–17h59 → après-midi
 *   • 18h–4h59  → soir (isNight)
 */
export function getDayPeriod(date = new Date()) {
  const h = date.getHours();
  if (h >= 5 && h < 12) return { period: "morning", label: "matinée", isNight: false };
  if (h >= 12 && h < 18) return { period: "afternoon", label: "après-midi", isNight: false };
  return { period: "evening", label: "soirée", isNight: true };
}

/** Convenience — picks the correct icon based on the period. */
export default function WeatherIcon({ isNight, size = 24, className = "" }) {
  return isNight
    ? <MoonIcon size={size} className={className} />
    : <SunIcon size={size} className={className} />;
}
